/* eslint-disable react-refresh/only-export-components */
import { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { 
  PublicClientApplication, 
  AuthenticationResult,
  Configuration,
  AccountInfo,
} from '@azure/msal-browser';
import { MsalProvider } from '@azure/msal-react';
import axios from 'axios';

interface User {
  id: string;
  email: string;
  name?: string;
  role: 'User' | 'Administrator';
}

interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: () => Promise<void>;
  logout: () => Promise<void>;
  getAccessToken: () => string | null;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

// API URL with fallback for Vite proxy
const API_URL = import.meta.env.VITE_API_URL || '';

// MSAL configuration will be fetched from backend
let msalInstance: PublicClientApplication | null = null;

interface AuthProviderProps {
  children: ReactNode;
}

export function AuthProvider({ children }: AuthProviderProps) {
  const [user, setUser] = useState<User | null>(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [msalReady, setMsalReady] = useState(false);
  const [jwtToken, setJwtToken] = useState<string | null>(null);

  // Initialize MSAL with configuration from backend
  useEffect(() => {
    const initMsal = async () => {
      try {
        // Check if we're returning from a redirect (check URL hash for auth response)
        const hasAuthResponse = window.location.hash.includes('code=') || 
                                window.location.hash.includes('error=') ||
                                sessionStorage.getItem('msal_login_in_progress') === 'true';
        
        console.log('AuthContext initializing...', { 
          hasAuthResponse, 
          hash: window.location.hash || 'empty',
          fullHash: window.location.hash.substring(0, 50) + '...',
          pathname: window.location.pathname,
          href: window.location.href.substring(0, 100) + '...'
        });

        // Fetch Azure Entra ID configuration from backend
        const response = await axios.get(`${API_URL}/api/auth/config`);
        const { tenantId, clientId } = response.data.data;

        // Use /login as redirect URI so we land on the login page (not protected) after auth
        // This prevents ProtectedRoute from redirecting and losing the auth hash
        const redirectUri = `${window.location.origin}/login`;
        
        console.log('Initializing MSAL with:', { tenantId, clientId, redirectUri });

        const msalConfig: Configuration = {
          auth: {
            clientId: clientId,
            authority: `https://login.microsoftonline.com/${tenantId}`,
            redirectUri: redirectUri,
            navigateToLoginRequestUrl: false, // Don't navigate back to login page after redirect
          },
          cache: {
            cacheLocation: 'localStorage',
            storeAuthStateInCookie: true, // Enable for IE11/Edge compatibility and redirect flow
          },
          system: {
            loggerOptions: {
              loggerCallback: (level, message, containsPii) => {
                if (containsPii) return;
                switch (level) {
                  case 0: console.error('MSAL:', message); break;
                  case 1: console.warn('MSAL:', message); break;
                  case 2: console.info('MSAL:', message); break;
                  case 3: console.debug('MSAL:', message); break;
                }
              },
              logLevel: 3, // Verbose logging for debugging
            },
          },
        };

        msalInstance = new PublicClientApplication(msalConfig);
        await msalInstance.initialize();

        // Handle redirect response (for redirect login flow)
        // This MUST be called on every page load to handle the redirect response
        console.log('Calling handleRedirectPromise...');
        try {
          const redirectResponse = await msalInstance.handleRedirectPromise();
          console.log('handleRedirectPromise result:', redirectResponse ? 'Response received' : 'No response');
          
          if (redirectResponse) {
            console.log('Redirect response received:', redirectResponse.account?.username);
            msalInstance.setActiveAccount(redirectResponse.account);
            await handleAuthentication(redirectResponse.account!);
            sessionStorage.removeItem('msal_login_in_progress');
            setMsalReady(true);
            return;
          }
        } catch (redirectError) {
          console.error('Error handling redirect:', redirectError);
          sessionStorage.removeItem('msal_login_in_progress');
          setIsLoading(false);
          setMsalReady(true);
          return;
        }

        setMsalReady(true);

        // Check if user is already authenticated
        const accounts = msalInstance.getAllAccounts();
        console.log('Existing accounts:', accounts.length);
        if (accounts.length > 0) {
          console.log('Found existing account:', accounts[0].username);
          msalInstance.setActiveAccount(accounts[0]);
          await handleAuthentication(accounts[0]);
        } else {
          setIsLoading(false);
        }
      } catch (error) {
        console.error('Failed to initialize MSAL:', error);
        setIsLoading(false);
        setMsalReady(true);
      }
    };

    initMsal();
  }, []);

  // Handle authentication after MSAL login
  const handleAuthentication = async (account: AccountInfo) => {
    try {
      if (!msalInstance) return;

      // Get tokens from MSAL
      const tokenRequest = {
        scopes: ['User.Read'],
        account: account,
      };

      console.log('Acquiring token silently...');
      const response: AuthenticationResult = await msalInstance.acquireTokenSilent(tokenRequest);
      console.log('Token acquired, exchanging for JWT...');
      
      // Exchange Azure AD ID token for our JWT
      // We use idToken instead of accessToken because:
      // - accessToken is for calling Microsoft Graph API and is opaque
      // - idToken is a JWT containing user claims (oid, preferred_username, name, etc.)
      const jwtResponse = await axios.post(`${API_URL}/api/auth/token`, {
        idToken: response.idToken,
      });

      console.log('JWT received successfully');
      const { token, user: userData } = jwtResponse.data.data;
      setJwtToken(token);
      setUser(userData);
      setIsAuthenticated(true);

      // Store token in localStorage for API requests
      localStorage.setItem('jwt_token', token);
      
      // Redirect to home page after successful authentication
      if (window.location.pathname === '/login') {
        window.location.href = '/';
      }
    } catch (error: unknown) {
      console.error('Authentication failed:', error);
      
      // Log more details about the error
      const axiosError = error as { 
        response?: { status?: number; data?: { error?: { message?: string } } };
        message?: string;
      };
      
      if (axiosError.response) {
        console.error('Token exchange failed:', {
          status: axiosError.response.status,
          message: axiosError.response.data?.error?.message,
        });
      }
      
      setUser(null);
      setIsAuthenticated(false);
      throw error; // Re-throw so the login function can handle it
    } finally {
      setIsLoading(false);
    }
  };

  // Login function
  const login = async () => {
    if (!msalInstance) {
      throw new Error('MSAL not initialized');
    }

    try {
      setIsLoading(true);
      const loginRequest = {
        scopes: ['User.Read'],
      };

      console.log('Starting MSAL login...');
      console.log('MSAL Config:', {
        clientId: msalInstance.getConfiguration().auth.clientId,
        authority: msalInstance.getConfiguration().auth.authority,
        redirectUri: msalInstance.getConfiguration().auth.redirectUri,
      });
      
      // In production, use redirect flow directly as popups are often blocked
      // In development, try popup first for better UX
      const isProduction = window.location.hostname !== 'localhost';
      
      if (isProduction) {
        console.log('Production environment detected, using redirect flow...');
        sessionStorage.setItem('msal_login_in_progress', 'true');
        await msalInstance.loginRedirect(loginRequest);
        return; // Will redirect, so no need to continue
      }
      
      // Development: Try popup first, fall back to redirect if popup fails
      try {
        console.log('Trying popup login...');
        const response: AuthenticationResult = await msalInstance.loginPopup(loginRequest);
        console.log('MSAL login successful, setting active account...');
        msalInstance.setActiveAccount(response.account);
        await handleAuthentication(response.account!);
      } catch (popupError: unknown) {
        const msalError = popupError as { errorCode?: string; errorMessage?: string };
        console.error('Popup login failed:', msalError.errorCode, msalError.errorMessage);
        
        // If popup was blocked or cancelled, try redirect flow
        if (msalError.errorCode === 'popup_window_error' || 
            msalError.errorCode === 'user_cancelled' ||
            msalError.errorCode === 'empty_window_error') {
          console.log('Popup failed, trying redirect flow instead...');
          // Store that we're in the middle of a redirect login
          sessionStorage.setItem('msal_login_in_progress', 'true');
          await msalInstance.loginRedirect(loginRequest);
          return; // Will redirect, so no need to continue
        }
        throw popupError;
      }
    } catch (error: unknown) {
      // Enhanced error logging for SSO debugging
      console.error('Login failed:', error);
      
      // Extract detailed error information from MSAL errors
      const msalError = error as { errorCode?: string; errorMessage?: string; message?: string; name?: string };
      const errorDetails = {
        errorCode: msalError.errorCode,
        errorMessage: msalError.errorMessage || msalError.message,
        errorName: msalError.name,
      };
      console.error('MSAL Error Details:', errorDetails);
      
      setIsLoading(false);
      throw error;
    }
  };

  // Logout function
  const logout = async () => {
    if (!msalInstance) return;

    try {
      setIsLoading(true);
      localStorage.removeItem('jwt_token');
      setUser(null);
      setIsAuthenticated(false);
      setJwtToken(null);
      
      await msalInstance.logoutPopup();
    } catch (error) {
      console.error('Logout failed:', error);
    } finally {
      setIsLoading(false);
    }
  };

  // Get access token for API requests
  const getAccessToken = () => {
    return jwtToken || localStorage.getItem('jwt_token');
  };

  const value: AuthContextType = {
    user,
    isAuthenticated,
    isLoading,
    login,
    logout,
    getAccessToken,
  };

  // Wait for MSAL to be ready before rendering children
  if (!msalReady) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto mb-4"></div>
          <p className="text-gray-600 dark:text-gray-400">Initializing authentication...</p>
        </div>
      </div>
    );
  }

  return (
    <AuthContext.Provider value={value}>
      <MsalProvider instance={msalInstance!}>
        {children}
      </MsalProvider>
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
